// src/components/FlashcardsPage.js

import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Importa o serviço de API configurado

const FlashcardsPage = ({ onVoltar, onNavigate, user }) => {
    const [hasAccess, setHasAccess] = useState(false);
    const [accessChecked, setAccessChecked] = useState(false);

    useEffect(() => {
        const checkAccess = async () => {
            try {
                // Check if user has access to flashcards section
                const accessResponse = await api.get('/api/section-access/check/flashcards');
                if (accessResponse.data.success && accessResponse.data.hasAccess) {
                    setHasAccess(true);
                } else {
                    setHasAccess(false);
                }
            } catch (err) {
                console.error('Erro ao verificar acesso:', err);
                setHasAccess(false);
            } finally {
                setAccessChecked(true);
            }
        };

        checkAccess();
    }, []);

    // Função para lidar com o upgrade
    const handleUpgrade = () => {
        // Redirecionar para a página de vendas
        onVoltar('venda-premium');
    };

    if (!accessChecked) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">Verificando acesso...</p>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="min-h-screen bg-[#F8F6FC]">
                <div className="container mx-auto p-4 sm:p-6 md:p-8">
                    {/* Header */}
                    <header className="flex items-center justify-between mb-8">
                        <div className="flex items-center">
                            <button
                                onClick={onVoltar}
                                className="flex items-center text-[#7B2CDB] font-semibold hover:text-[#9B4DFF] transition-colors mr-6"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                                </svg>
                                Voltar
                            </button>
                            <h1 className="text-3xl font-bold text-gray-800">Meus Flashcards</h1>
                        </div>
                    </header>

                    {/* Conteúdo principal com blur */}
                    <div className="max-w-4xl mx-auto blur-sm pointer-events-none">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Card: Revisar Flashcards */}
                            <div
                                className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white transform transition-all duration-300 hover:scale-105 hover:shadow-2xl flex flex-col"
                            >
                                <div className="flex items-center mb-4">
                                    <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-bold">Revisar Flashcards</h3>
                                </div>
                                <p className="flex-grow text-violet-100">
                                    Revise seus flashcards usando o sistema de repetição espaçada para melhor retenção
                                </p>
                                <div className="mt-4 text-right">
                                    <span className="inline-block bg-white text-violet-600 px-4 py-2 rounded-full text-sm font-bold">
                                        Iniciar Revisão
                                    </span>
                                </div>
                            </div>

                            {/* Card: Por Matéria */}
                            <div
                                className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white transform transition-all duration-300 hover:scale-105 hover:shadow-2xl flex flex-col"
                            >
                                <div className="flex items-center mb-4">
                                    <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-bold">Por Matéria</h3>
                                </div>
                                <p className="flex-grow text-violet-100">
                                    Explore seus flashcards organizados por matérias, tópicos e subtópicos
                                </p>
                                <div className="mt-4 text-right">
                                    <span className="inline-block bg-white text-violet-600 px-4 py-2 rounded-full text-sm font-bold">
                                        Navegar por Matéria
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Informação adicional */}
                        <div className="mt-12 bg-white rounded-xl p-6 shadow-md border border-violet-200">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Sobre os Flashcards</h2>
                            <p className="text-gray-600 mb-4">
                                Os flashcards são uma excelente ferramenta para memorização e revisão eficaz.
                                Utilize a opção de revisão para reforçar seu aprendizado com base no tempo de dificuldade.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                                <div className="text-center p-4 bg-violet-50 rounded-lg">
                                    <div className="text-violet-600 font-bold text-lg">Fácil</div>
                                    <p className="text-sm text-gray-600">Demora mais para aparecer novamente</p>
                                </div>
                                <div className="text-center p-4 bg-violet-50 rounded-lg">
                                    <div className="text-violet-600 font-bold text-lg">Bom</div>
                                    <p className="text-sm text-gray-600">Aparece após metade dos outros flashcards</p>
                                </div>
                                <div className="text-center p-4 bg-violet-50 rounded-lg">
                                    <div className="text-violet-600 font-bold text-lg">Difícil</div>
                                    <p className="text-sm text-gray-600">Aparece novamente em breve</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Overlay com mensagem de acesso */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="text-center p-8 max-w-md bg-white rounded-xl shadow-lg">
                            <h2 className="text-2xl font-bold text-purple-600 mb-4">É necessário atualizar o seu plano para ter acesso</h2>
                            <div className="mb-4">
                                <button
                                    onClick={handleUpgrade}
                                    className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-full transition-colors duration-300 shadow-lg"
                                >
                                    Atualize seu Plano
                                </button>
                            </div>
                            <button
                              onClick={() => window.history.back()}
                              className="text-gray-600 hover:text-gray-800 font-medium text-sm underline"
                            >
                              Voltar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F6FC]">
            <div className="container mx-auto p-4 sm:p-6 md:p-8">
                {/* Header */}
                <header className="flex items-center justify-between mb-8">
                    <div className="flex items-center">
                        <button
                            onClick={onVoltar}
                            className="flex items-center text-[#7B2CDB] font-semibold hover:text-[#9B4DFF] transition-colors mr-6"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                            </svg>
                            Voltar
                        </button>
                        <h1 className="text-3xl font-bold text-gray-800">Meus Flashcards</h1>
                    </div>
                </header>

                {/* Cards de Opções */}
                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Card: Revisar Flashcards */}
                        <div
                            className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl flex flex-col"
                            onClick={() => onNavigate('flashcard-revision')}
                        >
                            <div className="flex items-center mb-4">
                                <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold">Revisar Flashcards</h3>
                            </div>
                            <p className="flex-grow text-violet-100">
                                Revise seus flashcards usando o sistema de repetição espaçada para melhor retenção
                            </p>
                            <div className="mt-4 text-right">
                                <span className="inline-block bg-white text-violet-600 px-4 py-2 rounded-full text-sm font-bold">
                                    Iniciar Revisão
                                </span>
                            </div>
                        </div>

                        {/* Card: Por Matéria */}
                        <div
                            className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl flex flex-col"
                            onClick={() => onNavigate('flashcards-by-subject')}
                        >
                            <div className="flex items-center mb-4">
                                <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold">Por Matéria</h3>
                            </div>
                            <p className="flex-grow text-violet-100">
                                Explore seus flashcards organizados por matérias, tópicos e subtópicos
                            </p>
                            <div className="mt-4 text-right">
                                <span className="inline-block bg-white text-violet-600 px-4 py-2 rounded-full text-sm font-bold">
                                    Navegar por Matéria
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Informação adicional */}
                    <div className="mt-12 bg-white rounded-xl p-6 shadow-md border border-violet-200">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Sobre os Flashcards</h2>
                        <p className="text-gray-600 mb-4">
                            Os flashcards são uma excelente ferramenta para memorização e revisão eficaz.
                            Utilize a opção de revisão para reforçar seu aprendizado com base no tempo de dificuldade.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                            <div className="text-center p-4 bg-violet-50 rounded-lg">
                                <div className="text-violet-600 font-bold text-lg">Fácil</div>
                                <p className="text-sm text-gray-600">Demora mais para aparecer novamente</p>
                            </div>
                            <div className="text-center p-4 bg-violet-50 rounded-lg">
                                <div className="text-violet-600 font-bold text-lg">Bom</div>
                                <p className="text-sm text-gray-600">Aparece após metade dos outros flashcards</p>
                            </div>
                            <div className="text-center p-4 bg-violet-50 rounded-lg">
                                <div className="text-violet-600 font-bold text-lg">Difícil</div>
                                <p className="text-sm text-gray-600">Aparece novamente em breve</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AdSense ad after the main content */}
            </div>
        </div>
    );
};

export default FlashcardsPage;